import ChartHeader from '../ChartHeader';
import NoResponsesAvailableYet from '../NoResponsesAvailableYet';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5wc from '@amcharts/amcharts5/wc';

const WORDLIMIT = 100;
const MAXWORDLENGTH = 2;

const OpenEndedInsightsChart = ({ data = [], onRefresh, isFirstLoad }) => {
  const chartRef = useRef(null);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const openEndedQuestions = data || [];

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    if (onRefresh) {
      await onRefresh();
    }
    setTimeout(() => setIsManualRefreshing(false), 500);
  };

  // Initialize first question when component mounts
  useEffect(() => {
    if (openEndedQuestions.length > 0 && !selectedQuestion) {
      setSelectedQuestion(openEndedQuestions?.[0]?._id);
    }
  }, []);

  useLayoutEffect(() => {
    if (!openEndedQuestions || openEndedQuestions.length === 0 || !selectedQuestion) {
      return;
    }

    const question = openEndedQuestions.find((q) => q?._id === selectedQuestion);
    if (!question) return;

    // Parse and count word frequencies
    const wordFrequency = {};
    const responses = question?.responses || [];

    responses.forEach((response) => {
      if (!response.text) return;

      // Extract words: letters + optional apostrophes (e.g., can't)
      const words = response.text.toLowerCase().match(/[a-z']+/g);

      if (!words) return;

      words.forEach((word) => {
        // Must have at least 2 letters (numbers and junk excluded automatically)
        if (word.length < MAXWORDLENGTH) return;

        wordFrequency[word] = (wordFrequency[word] || 0) + 1;
      });
    });

    // Convert to array and sort by frequency
    const wordCloudData = Object.entries(wordFrequency)
      .map(([word, frequency]) => ({
        word,
        frequency,
        value: frequency
      }))
      .sort((a, b) => b?.frequency - a?.frequency)
      .slice(0, WORDLIMIT); // Limit to top 100 words

    if (wordCloudData.length === 0) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo.dispose();

    const container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: root.verticalLayout
      })
    );

    const series = container.children.push(
      am5wc.WordCloud.new(root, {
        categoryField: 'word',
        valueField: 'value',
        maxFontSize: am5.percent(35),
        minFontSize: am5.percent(3)
      })
    );

    series.labels.template.setAll({
      paddingTop: 20,
      paddingBottom: 20,
      paddingLeft: 20,
      paddingRight: 20,
      fontFamily: 'Poppins, sans-serif'
    });

    // Color mapping based on frequency
    series.labels.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        const value = dataItem.get('value');
        const maxValue = Math.max(...wordCloudData.map((d) => d.value));
        const ratio = value / maxValue;

        if (ratio > 0.7) return am5.color(0x1976d2); // Dark blue for high frequency
        if (ratio > 0.4) return am5.color(0x42a5f5); // Medium blue
        return am5.color(0x64b5f6); // Light blue for lower frequency
      }
      return am5.color(0x1976d2);
    });

    series.data.setAll(wordCloudData);

    return () => root.dispose();
  }, [selectedQuestion, openEndedQuestions]);

  return (
    <Box>
      <ChartHeader
        title='Open-ended insights'
        description='Word clouds and text insights for open-ended questions'
        showRefreshButton={openEndedQuestions && openEndedQuestions.length > 0}
        handleManualRefresh={handleManualRefresh}
        isRefreshing={isManualRefreshing}
      />

      {!openEndedQuestions || openEndedQuestions?.length === 0 ? (
        <Box sx={{ mt: 2 }}>
          <NoResponsesAvailableYet data={openEndedQuestions} onRefresh={onRefresh} />
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ maxWidth: 500 }}>
              <InputLabel>Select Question</InputLabel>
              <Select
                value={selectedQuestion}
                label='Select Question'
                onChange={(e) => setSelectedQuestion(e.target.value)}
              >
                {openEndedQuestions.map((q) => (
                  <MenuItem key={q._id} value={q._id}>
                    {q.questionText}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box ref={chartRef} style={{ width: '100%', height: 'calc(100vh - 350px)' }} />
        </Box>
      )}
    </Box>
  );
};

export default OpenEndedInsightsChart;
